"use client";

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  ChevronDown,
  ExternalLink,
  Plus,
  RefreshCw,
  ShieldCheck,
  Users,
} from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "@/app/_module/components/ui/button";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import {
  managedMarketingApi,
  type Campaign,
  type Workspace,
} from "./managedMarketingApi";
import {
  errorMessage,
  money,
  safeWebUrl,
  statusLabels,
} from "./marketingHelpers";
import CampaignRequestForm from "./CampaignRequestForm";
import CampaignDetailPanel from "./CampaignDetailPanel";
import s from "./MarketingWorkspace.module.css";

export default function MarketingWorkspace() {
  const { user } = useUserContext();
  const ownerId = user?.user?.id;
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["managed-marketing", ownerId, "workspace"],
    queryFn: managedMarketingApi.workspace,
    enabled: !!ownerId,
    staleTime: 30_000,
    retry: 1,
  });
  const [operator, setOperator] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [paymentReturn, setPaymentReturn] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const mobile = pathname.startsWith("/m/");
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const id = search.get("campaign");
    if (id && /^[0-9a-f-]{36}$/i.test(id)) setSelectedId(id);
    setPaymentReturn(search.get("funding"));
  }, []);
  const operations = useQuery({
    queryKey: ["managed-marketing", ownerId, "operations"],
    queryFn: managedMarketingApi.operations,
    enabled: operator && query.data?.isOperator === true,
    staleTime: 30_000,
    retry: 1,
  });
  const create = useMutation({
    mutationFn: managedMarketingApi.create,
    onSuccess: async (campaign) => {
      await client.invalidateQueries({
        queryKey: ["managed-marketing", ownerId],
      });
      setCreating(false);
      setSelectedId(campaign.id);
    },
  });
  function select(id: string) {
    setSelectedId((previous) => (previous === id ? null : id));
    requestAnimationFrame(() =>
      detailRef.current?.scrollIntoView({
        block: "start",
        behavior: "instant",
      }),
    );
  }
  const workspace = query.data;
  const campaigns = operator
    ? operations.data?.campaigns || []
    : workspace?.campaigns || [];
  const visible = campaigns.filter(
    (campaign) =>
      (filter === "all" || campaign.status === filter) &&
      `${campaign.name} ${campaign.profileSnapshot.name} ${campaign.states.join(" ")}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  return (
    <section className={s.workspace} aria-label="Licensedproducer marketing">
      <header className={s.masthead}>
        <div className={s.brand}>
          <img src="/brands/licensedproducer/logo.svg" alt="" />
          <div>
            <h1>Licensedproducer marketing</h1>
            <p>Your name. Our team behind your campaign.</p>
          </div>
        </div>
        {workspace && (
          <div className="flex flex-wrap gap-2">
            {workspace.isOperator && (
              <Button
                variant="outline"
                onClick={() => {
                  setOperator(!operator);
                  setSelectedId(null);
                  setCreating(false);
                }}
              >
                <ShieldCheck />
                {operator ? "My campaigns" : "Staff workspace"}
              </Button>
            )}
            {!operator && !creating && (
              <Button
                className={s.primary}
                onClick={() => {
                  setCreating(true);
                  setSelectedId(null);
                }}
                disabled={
                  workspace.readiness.databaseReady === false ||
                  !workspace.readiness.profileAvailable ||
                  !workspace.profile?.licensedStates.length
                }
              >
                <Plus />
                New campaign
              </Button>
            )}
          </div>
        )}
      </header>
      {query.isPending && (
        <p role="status" className="py-16 text-sm text-muted-foreground">
          Loading your marketing workspace…
        </p>
      )}
      {query.isError && (
        <div className={s.notice} role="alert">
          <h2 className="font-medium">We couldn’t load your campaigns.</h2>
          <p className="my-2">{errorMessage(query.error)}</p>
          <Button variant="outline" onClick={() => query.refetch()}>
            <RefreshCw />
            Try again
          </Button>
        </div>
      )}
      {workspace && (
        <>
          {paymentReturn && (
            <p role="status" className={s.notice}>
              {paymentReturn === "processing"
                ? "Payment submitted. Your balance updates after Stripe confirms payment."
                : "You returned from checkout. Only confirmed payments appear in your balance."}{" "}
              <button
                type="button"
                className="ml-2 underline underline-offset-4"
                onClick={() => {
                  client.invalidateQueries({
                    queryKey: ["managed-marketing", ownerId],
                  });
                  setPaymentReturn(null);
                }}
              >
                Refresh records
              </button>
            </p>
          )}
          {operator ? (
            <div className="mb-5">
              <h2 className="text-xl font-semibold">Campaign operations</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Review requests, prepare creative, and keep each producer’s
                spend and results up to date.
              </p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                <a
                  className="inline-flex min-h-11 items-center underline underline-offset-4"
                  href="/brands/licensedproducer-brand-kit.zip"
                  download
                >
                  Licensedproducer brand kit
                </a>
                <a
                  className="inline-flex min-h-11 items-center underline underline-offset-4"
                  href="/brands/insuracentral-brand-kit.zip"
                  download
                >
                  InsuraCentral brand kit
                </a>
              </div>
            </div>
          ) : (
            <ProfileRow workspace={workspace} mobile={mobile} />
          )}
          {!operator && workspace.readiness.accountLinked === false ? (
            /* Not connected is the agent's own two-click fix, so send them to the
               settings card instead of listing it among the team's config issues.
               Same deep link Request Review uses. */
            <div className={s.notice}>
              <p className="font-medium">
                Connect your Licensedproducer account to run campaigns.
              </p>
              <p className="my-2">
                Campaigns feature your verified Licensedproducer profile, so the
                accounts have to be linked before you can request one.
              </p>
              <Button
                variant="outline"
                onClick={() =>
                  router.push(
                    "/settings?tab=account&accountTab=profile&connect=licensedproducer",
                  )
                }
              >
                Connect Licensedproducer
              </Button>
            </div>
          ) : (
            !operator &&
            workspace.readiness.issues.length > 0 && (
              <div className={s.notice}>
                <ul>
                  {workspace.readiness.issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              </div>
            )
          )}
          {creating ? (
            <CampaignRequestForm
              workspace={workspace}
              pending={create.isPending}
              onSubmit={async (body) => {
                await create.mutateAsync(body);
              }}
              onCancel={() => setCreating(false)}
            />
          ) : (
            <>
              {!operator && campaigns.length === 0 && (
                <div className={s.intro}>
                  <div className={s.introCopy}>
                    <span className="text-xs font-medium uppercase tracking-widest">
                      Managed by Licensedproducer
                    </span>
                    <h2>Be the producer they already recognize.</h2>
                    <p>
                      A campaign featuring you, sent to the places you’re
                      licensed to serve. We handle the creative, placement, and
                      day-to-day management.
                    </p>
                    <Button
                      className={`${s.primary} mt-6`}
                      disabled={
                        workspace.readiness.databaseReady === false ||
                        !workspace.readiness.profileAvailable ||
                        !workspace.profile?.licensedStates.length
                      }
                      onClick={() => setCreating(true)}
                    >
                      Plan your first campaign
                      <ArrowRight />
                    </Button>
                  </div>
                  <ul className={s.introAside}>
                    <li>
                      <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
                      <div>
                        <strong>You approve it before it runs.</strong>
                        <p>
                          Your photo, your profile, and a clear coverage
                          message. Review the exact creative first.
                        </p>
                      </div>
                    </li>
                    <li>
                      <Users className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
                      <div>
                        <strong>Inquiries go to your CRM.</strong>
                        <p>
                          Campaign leads arrive under your account, ready for
                          your follow-up.
                        </p>
                      </div>
                    </li>
                    <li>
                      <div>
                        <strong>A budget you can follow.</strong>
                        <p>
                          See confirmed funding, reported ad spend, and
                          remaining funds for every campaign.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              )}
              {campaigns.length > 0 && <CampaignTotals campaigns={campaigns} />}
              {(campaigns.length > 0 || operator) && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold">
                    {operator ? "All campaigns" : "Your campaigns"}{" "}
                    <span className="ml-1 text-sm font-normal text-muted-foreground">
                      {campaigns.length}
                    </span>
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <input
                      className={`${s.field} !w-auto max-w-full`}
                      aria-label="Search campaigns"
                      placeholder="Search campaigns"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <select
                      className={`${s.field} !w-auto max-w-full`}
                      aria-label="Filter campaign status"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    >
                      <option value="all">All statuses</option>
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {operator && operations.isPending && (
                <p role="status">Loading campaign requests…</p>
              )}
              {operator && operations.isError && (
                <div className={s.notice} role="alert">
                  {errorMessage(operations.error)}
                  <Button
                    variant="outline"
                    className="ml-3"
                    onClick={() => operations.refetch()}
                  >
                    Try again
                  </Button>
                </div>
              )}
              <div>
                {visible.map((campaign) => (
                  <button
                    key={campaign.id}
                    type="button"
                    className={s.row}
                    aria-expanded={selectedId === campaign.id}
                    aria-controls={
                      selectedId === campaign.id ? "campaign-detail" : undefined
                    }
                    onClick={() => select(campaign.id)}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-base font-medium">
                        {campaign.name}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {operator ? `${campaign.profileSnapshot.name} · ` : ""}
                        {campaign.states.join(", ")} ·{" "}
                        {campaign.product.replaceAll("_", " ")}
                      </span>
                      <span className="mt-2 block sm:hidden">
                        <CampaignStatusBadge campaign={campaign} />
                      </span>
                    </span>
                    <span className={s.desktop}>
                      <CampaignStatusBadge campaign={campaign} />
                    </span>
                    <span
                      className={`${s.desktop} text-right text-sm tabular-nums`}
                    >
                      {money(campaign.spentCents)}
                      <span className="block text-xs text-muted-foreground">
                        spent
                      </span>
                    </span>
                    <span
                      className={`${s.desktop} text-right text-sm tabular-nums`}
                    >
                      {campaign.leadCount}
                      <span className="block text-xs text-muted-foreground">
                        leads
                      </span>
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 ${selectedId === campaign.id ? "rotate-180" : ""}`}
                    />
                  </button>
                ))}
              </div>
              {visible.length === 0 &&
                !operations.isPending &&
                (campaigns.length > 0 || operator) && (
                  <p className="py-12 text-center text-sm text-muted-foreground">
                    {search || filter !== "all"
                      ? "No campaigns match these filters."
                      : "No campaign requests yet."}
                  </p>
                )}
              <div ref={detailRef} id="campaign-detail">
                {selectedId && (
                  <CampaignDetailPanel
                    key={`${operator}-${selectedId}`}
                    id={selectedId}
                    ownerId={ownerId!}
                    operator={operator}
                    workspace={workspace}
                    mobile={mobile}
                    onClose={() => setSelectedId(null)}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

export function CampaignStatusBadge({ campaign }: { campaign: Campaign }) {
  return (
    <span className={s.status} data-status={campaign.status}>
      {statusLabels[campaign.status]}
    </span>
  );
}

function ProfileRow({
  workspace,
  mobile,
}: {
  workspace: Workspace;
  mobile: boolean;
}) {
  const profile = workspace.profile;
  const href = safeWebUrl(profile?.profileUrl);
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-5">
      <div className="flex items-center gap-3">
        {safeWebUrl(profile?.photoUrl) && (
          <img
            className="h-11 w-11 rounded-full object-cover"
            src={profile!.photoUrl!}
            alt=""
          />
        )}
        <div>
          <p className="text-sm font-medium">
            {profile?.name || "Your producer profile"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {profile
              ? `${profile.licensedStates.length} licensed ${profile.licensedStates.length === 1 ? "state" : "states"} · Campaigns managed by our team`
              : "Your linked Licensedproducer profile is used for every campaign."}
          </p>
        </div>
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-11 items-center gap-2 text-sm underline underline-offset-4"
        >
          View your profile
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ) : (
        <Link
          href={mobile ? "/m/settings" : "/settings"}
          className="inline-flex min-h-11 items-center text-sm underline underline-offset-4"
        >
          Open profile settings
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

function CampaignTotals({ campaigns }: { campaigns: Campaign[] }) {
  const sum = (field: "balanceCents" | "spentCents" | "leadCount") =>
    campaigns.reduce((total, campaign) => total + campaign[field], 0);
  return (
    <dl className={s.metrics}>
      <div>
        <dt>Available campaign funds</dt>
        <dd>{money(sum("balanceCents"))}</dd>
      </div>
      <div>
        <dt>Reported ad spend</dt>
        <dd>{money(sum("spentCents"))}</dd>
      </div>
      <div>
        <dt>Campaign leads</dt>
        <dd>{sum("leadCount").toLocaleString()}</dd>
      </div>
    </dl>
  );
}
